import React, { useState } from "react";
import { useDropzone } from "react-dropzone";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorIcon from "@mui/icons-material/Error";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Alert, Box, Button, IconButton, InputAdornment, TextField } from "@mui/material";

import { TranscoderTemplateUpload } from "../../services/api";

type FileUpload = {
  name: string;
  size: number;
  content: string;
};

const TemplateUploadForm: React.FC<{
  submit?: () => void;
  transcoderTemplate: TranscoderTemplateUpload;
  setTranscoderTemplate: (transcoderTemplate: TranscoderTemplateUpload) => void;
}> = ({ submit, transcoderTemplate, setTranscoderTemplate }) => {
  const [uploadedFile, setUploadedFile] = useState<FileUpload | null>(null);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    transcoderTemplate!.name = event.target.value;
    setTranscoderTemplate!(new TranscoderTemplateUpload(transcoderTemplate!.payload));
  };

  const handleJSONChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTranscoderTemplate!(new TranscoderTemplateUpload(event.target.value));
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "application/json": [".json"] },
    multiple: false,
    onDropAccepted: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      const textContent = await file.text();
      setUploadedFile({ name: file.name, size: file.size, content: textContent });
      setTranscoderTemplate!(new TranscoderTemplateUpload(textContent));
    },
  });

  const removeFile = () => {
    setUploadedFile(null);
  };

  const nameError = transcoderTemplate.hasError("name");
  const jsonPayloadError = transcoderTemplate.hasError("JSONPayload");

  return (
    <Box>
      <TextField
        label="Template Name"
        variant="outlined"
        autoComplete="off"
        error={!!nameError}
        helperText={nameError}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {nameError ? (
                <ErrorIcon sx={{ color: "red" }} data-testid={"error-icon-name"} />
              ) : (
                <CheckCircleIcon sx={{ color: "green" }} data-testid={"valid-icon-name"} />
              )}
            </InputAdornment>
          ),
        }}
        value={transcoderTemplate!.name}
        onChange={handleNameChange}
        fullWidth
        size="medium"
        margin="normal"
      />
      <TextField
        name="jsonobject"
        label="JSON Object"
        variant="outlined"
        error={!!jsonPayloadError}
        helperText={jsonPayloadError}
        InputProps={{
          endAdornment: (
            <InputAdornment sx={{ position: "absolute", top: 20, right: 10 }} position="end">
              {jsonPayloadError ? (
                <ErrorIcon sx={{ color: "red" }} data-testid={"error-icon-JSONPayload"} />
              ) : (
                <CheckCircleIcon sx={{ color: "green" }} data-testid={"valid-icon-JSONPayload"} />
              )}
            </InputAdornment>
          ),
        }}
        value={transcoderTemplate!.payload}
        onChange={handleJSONChange}
        fullWidth
        size="medium"
        margin="normal"
        multiline
        rows={10}
      />
      {transcoderTemplate.payload && (
        <Alert icon={false} severity="warning">
          The above JSON template will be uploaded.
        </Alert>
      )}
      {uploadedFile === null ? (
        <>
          <h3>Upload JSON file</h3>
          <Box
            sx={{
              ":hover": {
                cursor: "pointer",
              },
            }}
          >
            <div
              style={{ border: "1px dashed #e8e8e8", padding: "30px", textAlign: "center" }}
              {...getRootProps()}
            >
              <input accept=".json" data-testid={"fileUpload"} {...getInputProps()} />
              <p>
                <UploadFileIcon sx={{ color: "#2196f3" }} />
              </p>
              <p>Click to upload or drag and drop .json file</p>
            </div>
          </Box>
        </>
      ) : (
        <div style={{ marginTop: "20px" }}>
          <UploadFileIcon sx={{ float: "left", marginRight: "20px", color: "#2196f3" }} />
          <span data-testid={"uploadedFileName"} style={{ float: "left" }}>
            {uploadedFile.name}
            <div style={{ color: "#8c8c8c" }}>{uploadedFile.size / 1000}kb</div>
          </span>
          <IconButton
            data-testid={`fileRemove`}
            onClick={removeFile}
            sx={{
              marginLeft: "20px",
              color: "#707070",
              ":hover": {
                cursor: "pointer",
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      )}
      <Button
        sx={{ float: "right", mt: "20px" }}
        size="medium"
        variant="contained"
        disabled={transcoderTemplate!.hasErrors({ name: nameError!, JSONPayload: jsonPayloadError! })}
        onClick={submit}
      >
        Upload
      </Button>
    </Box>
  );
};
export default TemplateUploadForm;
